import React, { useState, useMemo } from "react";
import { Calendar as CalendarIcon, Clock, Video, ChevronLeft, ChevronRight, Check, User } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { timeSlots } from "../../data/mock";
import { postBooking } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";

export default function Consultation() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", topic: "" });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const MONTHS = t("consultation.months");
  const DAYS = t("consultation.days");

  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = first.getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, year]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y);
    setSelectedDate(null); setSelectedTime(null);
  };

  const isDisabled = (day) => {
    if (!day) return true;
    const d = new Date(year, month, day);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    const wd = d.getDay();
    return wd === 0 || wd === 6;
  };

  const selectDay = (day) => {
    if (isDisabled(day)) return;
    setSelectedDate({ day, month, year });
    setSelectedTime(null);
  };

  const handleField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const confirm = async () => {
    if (!selectedDate || !selectedTime) return;
    if (!form.name || !form.email) {
      toast({ title: t("consultation.missingTitle"), description: t("consultation.missingDesc") });
      return;
    }
    const dateStr = `${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, "0")}-${String(selectedDate.day).padStart(2, "0")}`;
    setSubmitting(true);
    try {
      await postBooking({ ...form, date: dateStr, time: selectedTime });
      toast({
        title: t("consultation.successTitle"),
        description: `${MONTHS[selectedDate.month]} ${selectedDate.day}, ${selectedDate.year} ${t("consultation.successDescPre")} ${selectedTime} ${t("consultation.successDescMid")} ${form.email}.`,
      });
      setForm({ name: "", email: "", phone: "", company: "", topic: "" });
      setSelectedDate(null); setSelectedTime(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      toast({ title: t("consultation.failTitle"), description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const dateLabel = selectedDate
    ? `${MONTHS[selectedDate.month]} ${selectedDate.day}, ${selectedDate.year}`
    : t("consultation.notSelected");

  const inputCls = "bg-white/[0.03] border-white/10 focus-visible:border-cyan-400/50 focus-visible:ring-cyan-400/20 text-white placeholder:text-slate-500 h-11";

  return (
    <section id="consultation" className="section-bg relative py-24 lg:py-32 overflow-hidden">
      <div className="orb bg-sky-500/15 w-[500px] h-[500px] top-[10%] left-[-160px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="kicker">{t("consultation.kicker")}</span>
          <h2 className="mt-5 font-display text-4xl lg:text-5xl font-bold text-white">
            {t("consultation.title1")} <span className="grad-text">{t("consultation.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-slate-300/90 text-lg">{t("consultation.description")}</p>
          <div className="mt-5 inline-flex items-center gap-4 text-sm text-slate-300">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-cyan-300" /> {t("consultation.minutes")}</div>
            <div className="w-px h-4 bg-white/10" />
            <div className="flex items-center gap-2"><Video className="w-4 h-4 text-cyan-300" /> {t("consultation.platform")}</div>
          </div>
          <p className="mt-2 text-xs text-slate-500">{t("consultation.timezone")}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 glass rounded-2xl p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-cyan-300" />
                <h3 className="text-white font-semibold">{t("consultation.selectDate")}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => changeMonth(-1)} className="w-9 h-9 rounded-lg border border-white/10 hover:border-cyan-400/40 text-slate-300 hover:text-white flex items-center justify-center transition">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="px-4 min-w-[170px] text-center font-semibold text-white">
                  {MONTHS[month]} {year}
                </div>
                <button onClick={() => changeMonth(1)} className="w-9 h-9 rounded-lg border border-white/10 hover:border-cyan-400/40 text-slate-300 hover:text-white flex items-center justify-center transition">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2">
              {DAYS.map((d) => (
                <div key={d} className="text-center text-xs uppercase tracking-widest text-slate-500 py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {grid.map((day, i) => {
                const disabled = isDisabled(day);
                const isSelected = selectedDate && day === selectedDate.day && month === selectedDate.month && year === selectedDate.year;
                return (
                  <button
                    key={i}
                    disabled={disabled}
                    onClick={() => selectDay(day)}
                    className={`h-11 rounded-lg text-sm font-medium transition ${
                      day === null
                        ? "invisible"
                        : isSelected
                        ? "bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg shadow-cyan-500/25"
                        : disabled
                        ? "text-slate-600 cursor-not-allowed"
                        : "bg-white/[0.03] hover:bg-cyan-500/10 border border-white/5 hover:border-cyan-400/30 text-slate-200"
                    }`}
                  >
                    {day || ""}
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-slate-500">{t("consultation.weekdaysOnly")}</p>

            {selectedDate && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-cyan-300" />
                  <h3 className="text-white font-semibold">{t("consultation.availableTimes")}</h3>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
                  {timeSlots.map((t2) => (
                    <button
                      key={t2}
                      onClick={() => setSelectedTime(t2)}
                      className={`h-10 rounded-lg text-sm font-medium transition border ${
                        selectedTime === t2
                          ? "bg-gradient-to-r from-sky-500 to-cyan-400 border-transparent text-white shadow-md shadow-cyan-500/25"
                          : "bg-white/[0.03] border-white/10 hover:border-cyan-400/40 text-slate-200"
                      }`}
                    >
                      {t2}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedTime && (
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-cyan-300" />
                  <h3 className="text-white font-semibold">{t("consultation.yourDetails")}</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input name="name" value={form.name} onChange={handleField} placeholder={t("consultation.fields.name")} className={inputCls} />
                  <Input name="email" type="email" value={form.email} onChange={handleField} placeholder={t("consultation.fields.emailPh")} className={inputCls} />
                  <Input name="phone" value={form.phone} onChange={handleField} placeholder={t("consultation.fields.phone")} className={inputCls} />
                  <Input name="company" value={form.company} onChange={handleField} placeholder={t("consultation.fields.company")} className={inputCls} />
                </div>
                <div className="mt-3">
                  <Textarea name="topic" value={form.topic} onChange={handleField} rows={3} placeholder={t("consultation.fields.topic")} className={`${inputCls} h-auto`} />
                </div>
              </div>
            )}
          </div>

          <div className="glass rounded-2xl p-8 h-fit lg:sticky lg:top-24">
            <h3 className="font-display text-lg font-semibold text-white mb-6">{t("consultation.yourSelection")}</h3>
            <div className="space-y-5">
              <SummaryRow label={t("consultation.date")} value={dateLabel} muted={!selectedDate} />
              <SummaryRow label={t("consultation.time")} value={selectedTime || t("consultation.notSelected")} muted={!selectedTime} />
              <SummaryRow label={t("consultation.platformLabel")} value={<span className="inline-flex items-center gap-2"><Video className="w-4 h-4 text-cyan-300" /> {t("consultation.platform")}</span>} />
              {form.name && <SummaryRow label={t("consultation.attendee")} value={form.name} />}
              {form.email && <SummaryRow label={t("consultation.email")} value={form.email} />}
            </div>
            <Button
              onClick={confirm}
              disabled={!selectedDate || !selectedTime || !form.name || !form.email || submitting}
              className="mt-8 w-full h-12 btn-primary rounded-xl font-semibold border-0 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4 mr-2" /> {submitting ? t("consultation.scheduling") : t("consultation.confirm")}
            </Button>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">{t("consultation.disclaimer")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SummaryRow({ label, value, muted }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`text-sm ${muted ? "text-slate-500" : "text-white"}`}>{value}</div>
    </div>
  );
}
